import type { CSSProperties } from 'react';
import { useTheme } from '@course-authoring/shared-types';

type ThemeTokens = ReturnType<typeof useTheme>['tokens'];

export type QuestionEditorVariant = 'block' | 'questionModal';

/**
 * Resolves the flags every question block editor derives from its render mode:
 * `isModal` (rendered inside the create-question modal, fully editable) and
 * `isManaged` (rendered in a block that references a bank question, so its
 * question fields are read-only), plus whether block-only authoring controls
 * are available. Kept in one place so these rules can't drift between editors.
 */
export function resolveQuestionEditorMode(
  variant: QuestionEditorVariant,
  block: { questionDefinitionId?: number },
  translationMode = false,
): { isModal: boolean; isManaged: boolean; showBlockAuthoringControls: boolean } {
  const isModal = variant === 'questionModal';
  return {
    isModal,
    isManaged: !isModal && block.questionDefinitionId !== undefined,
    showBlockAuthoringControls: !isModal && !translationMode,
  };
}

/** Read-only rendering of a bank-managed rich-text field's plain text. */
export function createReadOnlyTextStyle(tokens: ThemeTokens): CSSProperties {
  return {
    fontFamily: tokens.typography.fontFamily,
    fontSize: tokens.typography.body.size,
    color: tokens.text.muted,
    lineHeight: tokens.typography.body.lineHeight,
    whiteSpace: 'pre-wrap',
  };
}

/**
 * Muted italic marker used next to section labels of bank-managed fields.
 * Pass `when` to render conditionally (renders nothing when `when === false`),
 * so call sites don't repeat the `isManaged ? <ManagedInBankNote/> : null` guard.
 */
export function ManagedInBankNote({ when }: { when?: boolean } = {}): React.ReactElement | null {
  const { tokens } = useTheme();
  if (when === false) return null;
  return (
    <span
      style={{
        fontFamily: tokens.typography.fontFamily,
        fontSize: '12px',
        fontWeight: 400,
        fontStyle: 'italic',
        color: tokens.text.muted,
        textTransform: 'none',
        letterSpacing: 'normal',
      }}
    >
      Managed in Question Bank
    </span>
  );
}

ManagedInBankNote.displayName = 'ManagedInBankNote';

/**
 * The uppercase section-label row with an optional "Managed in Question Bank"
 * marker, shared by the Single Choice and Categorization editors (which use an
 * identical row). Likert/WCS use their own card-title rows and just render
 * `<ManagedInBankNote when={isManaged} />` inline.
 */
export function QuestionSectionLabel({
  label,
  managed,
  align = 'inline',
  trailing,
}: {
  label: string;
  managed: boolean;
  /** 'inline' (default): note sits right next to the label. 'split': note is pushed to the row's far right. */
  align?: 'inline' | 'split';
  /**
   * Extra status shown at the end of the row — NA-313 uses it for a field's
   * "Edited" badge and revert action. Rendered after the managed note, so a
   * section can be block-authored (`managed={false}`) and still carry status.
   */
  trailing?: React.ReactNode;
}): React.ReactElement {
  const { tokens } = useTheme();
  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'baseline',
        justifyContent: align === 'split' ? 'space-between' : 'flex-start',
        gap: tokens.spacing.sm,
        marginBottom: tokens.spacing.sm,
      }}
    >
      <span
        style={{
          fontSize: '12px',
          fontWeight: 700,
          color: tokens.text.muted,
          textTransform: 'uppercase',
          letterSpacing: '0.05em',
        }}
      >
        {label}
      </span>
      <ManagedInBankNote when={managed} />
      {trailing}
    </div>
  );
}

QuestionSectionLabel.displayName = 'QuestionSectionLabel';
